import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { 
  LuServer, LuArrowLeft, LuPlay, LuSquare, LuCpu, LuHardDrive,
  LuActivity, LuDatabase, LuNetwork, LuGlobe, LuUpload, LuDownload,
  LuFiles, LuFolderOpen, LuClock, LuDollarSign, LuInfo, LuExternalLink
} from 'react-icons/lu';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import Terminal from '../components/Terminal';
import DirectSshAccess from '../components/DirectSshAccess';
import ResourceRetirement from '../components/ResourceRetirement';
import ResourceContext from '../components/ResourceContext';
import ApplicationInventory from '../components/ApplicationInventory';
import WorkloadDiscovery from '../components/WorkloadDiscovery';
import { useScope } from '../contexts/ScopeContext';
import { capabilityForObservedState, capabilityReason, capabilityStateLabel } from '../utils/workHelpers';
import { usableCostBreakdown, usableResourceCost } from '../utils/costCoverage';
import { metricHistoryEmptyCopy } from '../utils/metricPresentation';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog';

// Resource type configuration
const RESOURCE_CONFIG = {
  vm: {
    icon: LuServer,
    label: 'Virtual Machine',
    color: 'amber',
    metrics: ['cpu', 'memory', 'disk', 'network'],
    hasActions: true
  },
  storage: {
    icon: LuHardDrive,
    label: 'Storage',
    color: 'teal',
    metrics: ['usage', 'objects', 'requests'],
    hasActions: false
  },
  database: {
    icon: LuDatabase,
    label: 'Database',
    color: 'emerald',
    metrics: ['connections', 'iops', 'cpu'],
    hasActions: true
  },
  network: {
    icon: LuNetwork,
    label: 'Network',
    color: 'blue',
    metrics: ['traffic', 'packets'],
    hasActions: false
  },
  load_balancer: {
    icon: LuGlobe,
    label: 'Load Balancer',
    color: 'violet',
    metrics: ['requests', 'latency', 'connections'],
    hasActions: true
  }
};

const COLORS = ['#f59e0b', '#14b8a6', '#3b82f6', '#8b5cf6', '#ef4444', '#22c55e'];
const metricValue = (value) => (value == null ? null : Number(value));

const MetricHistoryEmpty = ({ metricState, supportsHistory }) => {
  const copy = metricHistoryEmptyCopy({ metricState, supportsHistory });
  return (
    <div className="flex h-[300px] items-center justify-center px-6 text-center" role="status">
      <div className="max-w-md">
        <p className="font-medium text-zinc-300">{copy.title}</p>
        <p className="mt-2 text-sm leading-6 text-zinc-400">{copy.detail}</p>
      </div>
    </div>
  );
};

// VM Metrics Component
const VMMetrics = ({ metrics, metricsHistory, historyMeta, metricState }) => {
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 shadow-xl">
          <p className="text-xs text-zinc-400 mb-2">{payload[0]?.payload?.time}</p>
          {payload.map((entry) => (
            <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {metrics && !metrics.available && (
        <div role="status" className="mb-4 rounded-lg border border-zinc-700 bg-zinc-900 p-4 text-sm text-zinc-400">
          Live provider telemetry is unavailable. No values are being estimated.
        </div>
      )}
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <LuCpu className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">CPU Usage</span>
          </div>
          <p className="text-3xl font-bold">{metrics?.cpu_usage != null ? `${metrics.cpu_usage.toFixed(1)}%` : '—'}</p>
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <LuActivity className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Memory</span>
          </div>
          <p className="text-3xl font-bold">{metrics?.memory_usage != null ? `${metrics.memory_usage.toFixed(1)}%` : '—'}</p>
          {metrics?.memory_total_gb && (
            <p className="text-sm text-zinc-500 mt-1">
              {metrics.memory_used_gb?.toFixed(1) || 0} / {metrics.memory_total_gb?.toFixed(1)} GB
            </p>
          )}
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <LuHardDrive className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Disk</span>
          </div>
          <p className="text-3xl font-bold">{metrics?.disk_usage != null ? `${metrics.disk_usage.toFixed(1)}%` : '—'}</p>
          {metrics?.disk_total_gb && (
            <p className="text-sm text-zinc-500 mt-1">
              {metrics.disk_used_gb?.toFixed(1) || 0} / {metrics.disk_total_gb?.toFixed(1)} GB
            </p>
          )}
        </div>
      </div>

      {/* Performance Chart */}
      <div className="glass-card rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-zinc-200">Performance (Real-time)</h2>
          {historyMeta && (
            <div className="flex items-center gap-2">
              {historyMeta.supports_metric_history === false ? (
                <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-500">No History</span>
              ) : historyMeta.cache_status ? (
                <span className="text-xs px-2 py-0.5 rounded bg-teal-500/10 text-teal-400">{historyMeta.cache_status.toUpperCase()}</span>
              ) : null}
            </div>
          )}
        </div>
        {metricsHistory.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={metricsHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="time" stroke="#a1a1aa" fontSize={12} />
              <YAxis stroke="#a1a1aa" fontSize={12} domain={[0, 100]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="cpu" stroke="#3b82f6" strokeWidth={2} name="CPU %" dot={false} />
              <Line type="monotone" dataKey="memory" stroke="#8b5cf6" strokeWidth={2} name="Memory %" dot={false} />
              <Line type="monotone" dataKey="disk" stroke="#10b981" strokeWidth={2} name="Disk %" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <MetricHistoryEmpty metricState={metricState} supportsHistory={historyMeta?.supports_metric_history} />
        )}
      </div>

      {/* Network Stats */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-lg font-semibold text-zinc-200 mb-6">Network Activity</h2>
        <div className="grid grid-cols-2 gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <LuDownload className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Network In</p>
              <p className="text-2xl font-bold font-mono">
                {metrics?.network_in_bytes != null ? `${(metrics.network_in_bytes / 1024 / 1024).toFixed(2)} MB` : '—'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <LuUpload className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Network Out</p>
              <p className="text-2xl font-bold font-mono">
                {metrics?.network_out_bytes != null ? `${(metrics.network_out_bytes / 1024 / 1024).toFixed(2)} MB` : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Storage Metrics Component - Supports both aggregated S3 and individual storage
const StorageMetrics = ({ resource, metrics }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const config = resource?.config || {};
  const resourceCost = usableResourceCost(resource);
  const costBreakdown = usableCostBreakdown(resource);
  
  // Check if this is aggregated S3 storage
  const isS3Aggregated = config.service_type === 's3_aggregated';
  const buckets = config.buckets || [];
  const bucketCount = config.bucket_count || buckets.length || 0;
  
  // For non-aggregated storage (like block storage)
  const usedGB = config.used_space_gb || config.size_gb * (metrics?.disk_usage || 0) / 100 || 0;
  const totalGB = config.total_purchased_gb || config.size_gb || 250;
  const usagePercent = totalGB > 0 ? (usedGB / totalGB) * 100 : 0;
  
  // Storage class distribution (mock for now)
  const storageClassData = [
    { name: 'Standard', value: 70, color: '#3b82f6' },
    { name: 'Intelligent-Tiering', value: 15, color: '#8b5cf6' },
    { name: 'Standard-IA', value: 10, color: '#10b981' },
    { name: 'Glacier', value: 5, color: '#06b6d4' }
  ];

  // Tabs for S3 aggregated view
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'buckets', label: `Buckets (${bucketCount})` },
    { id: 'cost', label: 'Cost Breakdown' }
  ];

  if (isS3Aggregated) {
    return (
      <>
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-500 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <LuFolderOpen className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-sm text-zinc-400 uppercase tracking-wider">Buckets</span>
                </div>
                <p className="text-3xl font-bold">{bucketCount}</p>
                <p className="text-sm text-zinc-500 mt-1">in {resource?.region}</p>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <LuHardDrive className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-sm text-zinc-400 uppercase tracking-wider">Storage Class</span>
                </div>
                <p className="text-2xl font-bold">Standard</p>
                <p className="text-sm text-zinc-500 mt-1">primary class</p>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <LuDollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="text-sm text-zinc-400 uppercase tracking-wider">Monthly Cost</span>
                </div>
                <p className="text-3xl font-bold">{resourceCost === null ? 'Not available' : `$${resourceCost.toFixed(2)}`}</p>
                <p className="text-sm text-zinc-500 mt-1">aggregated</p>
              </div>
            </div>

            {/* Cost Breakdown */}
            {costBreakdown && (
              <div className="glass-card rounded-xl p-6 mb-6">
                <h2 className="text-lg font-semibold text-zinc-200 mb-6">Cost Breakdown</h2>
                <div className="space-y-4">
                  {costBreakdown.components.map((component, index) => (
                    <div key={index} className="flex items-start justify-between border-b border-zinc-800 pb-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-zinc-200">{component.name}</p>
                        <p className="text-xs text-zinc-500 mt-1">{component.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-green-400">${component.cost.toFixed(2)}</p>
                        <p className="text-xs text-zinc-500 mt-1">{component.percentage.toFixed(1)}%</p>
                      </div>
                    </div>
                  ))}
                </div>
                {costBreakdown.components.length > 1 && (
                  <div className="mt-6 pt-4 border-t-2 border-zinc-700 flex items-center justify-between">
                    <span className="text-sm font-semibold text-zinc-300">Total</span>
                    <span className="text-xl font-bold text-green-400">${costBreakdown.total.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Storage Class Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="glass-card rounded-xl p-6">
                <h2 className="text-lg font-semibold text-zinc-200 mb-6">Storage Class Distribution</h2>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={storageClassData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                      labelLine={false}
                    >
                      {storageClassData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Region Info */}
              <div className="glass-card rounded-xl p-6">
                <h2 className="text-lg font-semibold text-zinc-200 mb-6">Region Details</h2>
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">Region</span>
                    <span className="font-mono">{resource?.region}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">Pricing Model</span>
                    <span className="text-purple-400">Usage-Based</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">Total Buckets</span>
                    <span className="font-bold">{bucketCount}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-start gap-3">
                <LuInfo className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <h3 className="font-bold mb-2">About S3 Pricing</h3>
                  <p className="text-sm text-zinc-400">
                    S3 charges are based on <strong>total usage</strong> across all buckets, not per bucket. 
                    You pay for storage (GB), requests (PUT/GET), and data transfer (egress). 
                    Empty buckets have no cost. This view aggregates all {bucketCount} buckets in {resource?.region}.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Buckets Tab */}
        {activeTab === 'buckets' && (
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-zinc-200">All Buckets in {resource?.region}</h2>
              <span className="text-sm text-zinc-400">{bucketCount} buckets</span>
            </div>
            
            {buckets.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium text-sm">Bucket Name</th>
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium text-sm">Created</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium text-sm">Size</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium text-sm">Objects</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buckets.map((bucket, index) => (
                      <tr key={bucket.name || index} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <LuFolderOpen className="w-4 h-4 text-purple-400" />
                            <span className="font-mono text-sm">{bucket.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-zinc-400 text-sm">
                          {bucket.creation_date ? new Date(bucket.creation_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">
                          {bucket.size_bytes ? `${(bucket.size_bytes / 1024 / 1024 / 1024).toFixed(2)} GB` : '—'}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">
                          {bucket.object_count || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <LuFolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Bucket details not available</p>
                <p className="text-sm mt-1">Run discovery with CloudWatch permissions to see bucket sizes</p>
              </div>
            )}
          </div>
        )}

        {/* Cost Tab */}
        {activeTab === 'cost' && (
          <>
            <div className="glass-card rounded-xl p-6 mb-6">
              <h2 className="text-lg font-semibold text-zinc-200 mb-6">S3 Pricing Components</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-zinc-900 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <LuHardDrive className="w-4 h-4 text-blue-400" />
                    <span className="text-zinc-400 text-sm">Storage</span>
                  </div>
                  <p className="text-2xl font-mono font-bold">$0.023</p>
                  <p className="text-xs text-zinc-500">per GB/month (Standard)</p>
                </div>
                <div className="bg-zinc-900 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <LuUpload className="w-4 h-4 text-purple-400" />
                    <span className="text-zinc-400 text-sm">PUT Requests</span>
                  </div>
                  <p className="text-2xl font-mono font-bold">$0.005</p>
                  <p className="text-xs text-zinc-500">per 1,000 requests</p>
                </div>
                <div className="bg-zinc-900 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <LuDownload className="w-4 h-4 text-green-400" />
                    <span className="text-zinc-400 text-sm">GET Requests</span>
                  </div>
                  <p className="text-2xl font-mono font-bold">$0.0004</p>
                  <p className="text-xs text-zinc-500">per 1,000 requests</p>
                </div>
                <div className="bg-zinc-900 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <LuGlobe className="w-4 h-4 text-orange-400" />
                    <span className="text-zinc-400 text-sm">Data Transfer</span>
                  </div>
                  <p className="text-2xl font-mono font-bold">$0.09</p>
                  <p className="text-xs text-zinc-500">per GB (outbound)</p>
                </div>
              </div>
            </div>

            <div className="glass-card rounded-xl p-6">
              <h2 className="text-lg font-semibold text-zinc-200 mb-6">Cost Tips</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-zinc-900 rounded-lg">
                  <span className="text-2xl">💡</span>
                  <div>
                    <p className="font-medium">Use Lifecycle Policies</p>
                    <p className="text-sm text-zinc-400">Automatically move old objects to cheaper storage classes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-zinc-900 rounded-lg">
                  <span className="text-2xl">💡</span>
                  <div>
                    <p className="font-medium">Enable Intelligent-Tiering</p>
                    <p className="text-sm text-zinc-400">Automatically optimizes costs for data with unknown access patterns</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-zinc-900 rounded-lg">
                  <span className="text-2xl">💡</span>
                  <div>
                    <p className="font-medium">Clean Up Incomplete Uploads</p>
                    <p className="text-sm text-zinc-400">Abort multipart uploads that weren't completed to avoid charges</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // Non-aggregated storage (block storage, etc.)
  return (
    <>
      {/* Storage Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <LuHardDrive className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Storage Used</span>
          </div>
          <p className="text-3xl font-bold">{usedGB.toFixed(2)} GB</p>
          <p className="text-sm text-zinc-500 mt-1">of {totalGB} GB total</p>
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <LuFiles className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Objects</span>
          </div>
          <p className="text-3xl font-bold">{config.object_count || '—'}</p>
          <p className="text-sm text-zinc-500 mt-1">files stored</p>
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <LuActivity className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Usage</span>
          </div>
          <p className="text-3xl font-bold">{usagePercent.toFixed(1)}%</p>
          <div className="w-full h-2 bg-zinc-800 rounded-full mt-2">
            <div 
              className="h-2 bg-purple-500 rounded-full transition-all"
              style={{ width: `${Math.min(usagePercent, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Capacity Gauge */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-lg font-semibold text-zinc-200 mb-6">Capacity Usage</h2>
        <div className="flex items-center justify-center">
          <div className="relative w-48 h-48">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="96" cy="96" r="88"
                stroke="#27272a" strokeWidth="12" fill="none"
              />
              <circle
                cx="96" cy="96" r="88"
                stroke="#8b5cf6" strokeWidth="12" fill="none"
                strokeDasharray={`${usagePercent * 5.53} 553`}
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-4xl font-bold">{usagePercent.toFixed(0)}%</p>
              <p className="text-sm text-zinc-500">used</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Database Metrics Component
const DatabaseMetrics = ({ metrics, metricsHistory, historyMeta, metricState }) => {
  return (
    <>
      {/* Database Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <LuNetwork className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Connections</span>
          </div>
          <p className="text-3xl font-bold">{metrics?.connections ?? '—'}</p>
          <p className="text-sm text-zinc-500 mt-1">active connections</p>
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <LuActivity className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">IOPS</span>
          </div>
          <p className="text-3xl font-bold">{metrics?.iops ?? '—'}</p>
          <p className="text-sm text-zinc-500 mt-1">operations/sec</p>
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <LuCpu className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">CPU</span>
          </div>
          <p className="text-3xl font-bold">{metrics?.cpu_usage != null ? `${metrics.cpu_usage.toFixed(1)}%` : '—'}</p>
        </div>
      </div>

      {/* Query Performance Chart */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Database Performance</h2>
          {historyMeta && (
            <div>
              {historyMeta.supports_metric_history === false ? (
                <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-500">No History</span>
              ) : historyMeta.cache_status ? (
                <span className="text-xs px-2 py-0.5 rounded bg-teal-500/10 text-teal-400">{historyMeta.cache_status.toUpperCase()}</span>
              ) : null}
            </div>
          )}
        </div>
        {metricsHistory.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={metricsHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="time" stroke="#a1a1aa" fontSize={12} />
              <YAxis stroke="#a1a1aa" fontSize={12} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="connections" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Connections" />
              <Area type="monotone" dataKey="iops" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} name="IOPS" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <MetricHistoryEmpty metricState={metricState} supportsHistory={historyMeta?.supports_metric_history} />
        )}
      </div>
    </>
  );
};

// Network Metrics Component  
const NetworkMetrics = ({ resource, metrics }) => {
  const config = resource?.config || {};
  
  return (
    <>
      {/* Network Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <LuNetwork className="w-5 h-5 text-orange-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">CIDR Block</span>
          </div>
          <p className="text-2xl font-bold font-mono">{config.cidr_block || config.cidr || '—'}</p>
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <LuServer className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Connected Instances</span>
          </div>
          <p className="text-3xl font-bold">{config.instances?.length || 0}</p>
        </div>
      </div>

      {/* Network Info */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-xl font-bold mb-6">Network Configuration</h2>
        <div className="space-y-4">
          {config.dns_label && (
            <div className="flex justify-between py-2 border-b border-zinc-800">
              <span className="text-zinc-400">DNS Label</span>
              <span className="font-mono">{config.dns_label}</span>
            </div>
          )}
          {config.vpc_id && (
            <div className="flex justify-between py-2 border-b border-zinc-800">
              <span className="text-zinc-400">VPC ID</span>
              <span className="font-mono text-sm">{config.vpc_id}</span>
            </div>
          )}
          <div className="flex justify-between py-2 border-b border-zinc-800">
            <span className="text-zinc-400">Status</span>
            <span className="text-green-400">{config.lifecycle_state || resource?.status}</span>
          </div>
        </div>
        <p className="text-sm text-zinc-500 mt-4 flex items-center gap-2">
          <LuInfo className="w-4 h-4" />
          Virtual networks (VCN/VPC) typically have no direct cost
        </p>
      </div>
    </>
  );
};

// Load Balancer Metrics Component
const LoadBalancerMetrics = ({ metrics, metricsHistory, historyMeta, metricState }) => {
  return (
    <>
      {/* LB Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <LuActivity className="w-5 h-5 text-cyan-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Requests/sec</span>
          </div>
          <p className="text-3xl font-bold">{metrics?.requests_per_sec ?? '—'}</p>
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <LuClock className="w-5 h-5 text-yellow-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Avg Latency</span>
          </div>
          <p className="text-3xl font-bold">{metrics?.latency_ms != null ? `${metrics.latency_ms} ms` : '—'}</p>
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <LuNetwork className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-sm text-zinc-400 uppercase tracking-wider">Active Connections</span>
          </div>
          <p className="text-3xl font-bold">{metrics?.active_connections ?? '—'}</p>
        </div>
      </div>

      {/* Request Chart */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Request Traffic</h2>
          {historyMeta && (
            <div>
              {historyMeta.supports_metric_history === false ? (
                <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-500">No History</span>
              ) : historyMeta.cache_status ? (
                <span className="text-xs px-2 py-0.5 rounded bg-teal-500/10 text-teal-400">{historyMeta.cache_status.toUpperCase()}</span>
              ) : null}
            </div>
          )}
        </div>
        {metricsHistory.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={metricsHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="time" stroke="#a1a1aa" fontSize={12} />
              <YAxis stroke="#a1a1aa" fontSize={12} />
              <Tooltip />
              <Legend />
              <Bar dataKey="requests" fill="#06b6d4" name="Requests" />
              <Bar dataKey="errors" fill="#ef4444" name="Errors" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <MetricHistoryEmpty metricState={metricState} supportsHistory={historyMeta?.supports_metric_history} />
        )}
      </div>
    </>
  );
};

const ResourceDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const scope = useScope();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [resource, setResource] = useState(null);
  const [capabilities, setCapabilities] = useState({});
  const [capabilityMeta, setCapabilityMeta] = useState({});
  const [metrics, setMetrics] = useState(null);
  const [metricState, setMetricState] = useState('loading');
  const [metricsHistory, setMetricsHistory] = useState([]);
  const [metricHistoryMeta, setMetricHistoryMeta] = useState({ supports_metric_history: null, cache_status: null });
  const [activeTab, setActiveTab] = useState('overview');
  const [powerIntent, setPowerIntent] = useState(null);
  const [powerReason, setPowerReason] = useState('');
  const [applicationInventoryVersion, setApplicationInventoryVersion] = useState(0);
  const loadGeneration = useRef(0);

  const resourceConfig = RESOURCE_CONFIG[resource?.type] || RESOURCE_CONFIG.vm;
  const IconComponent = resourceConfig.icon;
  const can = (name) => capabilities?.[name]?.allowed === true;
  const observedPowerCapability = capabilityForObservedState(capabilities, resource?.status);
  const resourceCost = usableResourceCost(resource);
  const costBreakdown = usableCostBreakdown(resource);

  useEffect(() => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setResource(null);
    setCapabilities({});
    setCapabilityMeta({});
    setMetrics(null);
    setMetricState('loading');
    setMetricsHistory([]);
    setMetricHistoryMeta({ supports_metric_history: null, cache_status: null });
    setPowerIntent(null);
    setPowerReason('');
    loadResource(generation);
  }, [id, scope.version]);

  useEffect(() => {
    // Polling for metrics
    const metricsInterval = setInterval(() => {
      if (resource?.type === 'vm' || resource?.type === 'database' || resource?.type === 'load_balancer') {
        loadMetrics(resource.type);
      }
    }, 5000);

    return () => {
      clearInterval(metricsInterval);
    };
  }, [id, resource?.type]);

  // Load historical metrics (last 24 hours) to seed charts
  useEffect(() => {
    const fetchMetricHistory = async () => {
      try {
        if (!resource) return;
        if (!['vm', 'database', 'load_balancer'].includes(resource.type)) return;

        const end = new Date();
        const start = new Date(Date.now() - (24 * 60 * 60 * 1000)); // last 24 hours
        const res = await api.getMetricHistory(resource.id, start.toISOString(), end.toISOString());
        const payload = res.data && res.data.data ? res.data.data : [];

        // Capture metadata (supports/ cache status) if present
        try {
          const meta = {
            supports_metric_history: res.data && res.data.supports_metric_history !== undefined ? res.data.supports_metric_history : null,
            cache_status: res.data && res.data.cache_status ? res.data.cache_status : null
          };
          setMetricHistoryMeta(meta);
        } catch (err) {
          // ignore
        }

        if (payload && payload.length > 0) {
          // Map provider datapoints to chart-friendly format
          const mapped = payload.map(p => {
            const time = p.timestamp || p.time || p.date || p.ts || p[0] || '';
            const t = time ? new Date(time).toLocaleString() : new Date().toLocaleTimeString();
            // For VMs we expect a single value representing CPU (CloudWatch returns value)
            if (resource.type === 'vm') {
              return { time: t, cpu: metricValue(p.value ?? p.cpu) };
            }
            if (resource.type === 'database') {
              return { time: t, connections: metricValue(p.value ?? p.connections), iops: metricValue(p.iops) };
            }
            if (resource.type === 'load_balancer') {
              return { time: t, requests: metricValue(p.value ?? p.requests), errors: metricValue(p.errors) };
            }
            return { time: t, value: metricValue(p.value) };
          }).slice(-200);

          setMetricsHistory(mapped);
        }
      } catch (err) {
        // Non-blocking: continue using realtime polling if history fetch fails
        console.debug('Metric history fetch failed', err);
      }
    };

    fetchMetricHistory();
  }, [resource && resource.id]);

  const loadResource = async (generation = ++loadGeneration.current) => {
    try {
      const [response, capabilityResponse] = await Promise.all([
        api.getResource(id),
        api.getResourceCapabilities(id),
      ]);
      if (generation !== loadGeneration.current) return;
      setResource(response.data);
      setCapabilities(capabilityResponse.data?.capabilities || {});
      setCapabilityMeta(capabilityResponse.data || {});

      // Load metrics based on resource type
      if (['vm', 'database', 'load_balancer'].includes(response.data?.type)) {
        loadMetrics(response.data.type);
      }
    } catch (error) {
      if (error.code === 'ERR_CANCELED' || generation !== loadGeneration.current) return;
      toast.error('Failed to load resource');
      navigate('/resources');
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  };

  const handleSync = async (silent = false) => {
    try {
      if (!silent) setActionLoading(true);
      const response = await api.syncResource(id);
      if (response.data && response.data.resource) {
        setResource(response.data.resource);
        if (!silent) {
          response.data.synced
            ? toast.success('Resource synced with cloud')
            : toast.warning('Provider unavailable; last-known state preserved');
        }
      }
    } catch (error) {
      if (!silent) toast.error('Failed to sync resource');
      console.error('Sync error:', error);
    } finally {
      if (!silent) setActionLoading(false);
    }
  };

  const loadMetrics = async (resourceType = resource?.type) => {
    try {
      const response = await api.getMetrics(id);
      setMetrics(response.data);
      if (!response.data.available) {
        setMetricState('unavailable');
        return;
      }
      setMetricState('ready');
      
      // Build history based on resource type
      setMetricsHistory((prev) => {
        let newPoint = { time: new Date().toLocaleTimeString() };
        
        if (resourceType === 'vm') {
          newPoint = {
            ...newPoint,
            cpu: response.data.cpu_usage ?? null,
            memory: response.data.memory_usage ?? null,
            disk: response.data.disk_usage ?? null,
          };
        } else if (resourceType === 'database') {
          newPoint = {
            ...newPoint,
            connections: response.data.connections ?? null,
            iops: response.data.iops ?? null,
          };
        } else if (resourceType === 'load_balancer') {
          newPoint = {
            ...newPoint,
            requests: response.data.requests ?? null,
            errors: response.data.errors ?? null,
          };
        }
        
        return [...prev, newPoint].slice(-10);
      });
    } catch (error) {
      setMetricState('error');
      console.error('Failed to load metrics');
    }
  };

  const handleAction = (action) => {
    setPowerReason('');
    setPowerIntent({
      action,
      idempotencyKey: window.crypto?.randomUUID?.() || `power-${id}-${action}-${Date.now()}`,
    });
  };

  const submitPowerRequest = async () => {
    if (!powerIntent || powerReason.trim().length < 3) {
      toast.error('Please add a short reason for this request.');
      return;
    }
    try {
      setActionLoading(true);
      const response = await api.requestPower(
        scope.organization.id,
        scope.project.id,
        id,
        { action: powerIntent.action, reason: powerReason.trim() },
        powerIntent.idempotencyKey,
      );
      const status = response.data?.requisition?.status;
      toast.success(status === 'awaiting_approval'
        ? `${powerIntent.action} request submitted for approval`
        : `${powerIntent.action} request accepted safely`);
      setPowerIntent(null);
      setPowerReason('');
      await loadResource();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || `Failed to request ${powerIntent.action}`);
      if ([403, 409].includes(error.response?.status)) await loadResource();
    } finally {
      setActionLoading(false);
    }
  };

  // Render metrics based on resource type
  const renderMetrics = ({ historyMeta } = {}) => {
    switch (resource?.type) {
      case 'vm':
        return <VMMetrics metrics={metrics} metricsHistory={metricsHistory} historyMeta={historyMeta} metricState={metricState} />;
      case 'storage':
        return <StorageMetrics resource={resource} metrics={metrics} />;
      case 'database':
        return <DatabaseMetrics metrics={metrics} metricsHistory={metricsHistory} historyMeta={historyMeta} metricState={metricState} />;
      case 'network':
        return <NetworkMetrics resource={resource} metrics={metrics} />;
      case 'load_balancer':
        return <LoadBalancerMetrics metrics={metrics} metricsHistory={metricsHistory} historyMeta={historyMeta} metricState={metricState} />;
      default:
        return <VMMetrics metrics={metrics} metricsHistory={metricsHistory} historyMeta={historyMeta} metricState={metricState} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading resource...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto p-6 lg:p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <button
              onClick={() => navigate('/resources')}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <LuArrowLeft className="w-5 h-5" /> Back to Resources
            </button>
            <div className="flex flex-col items-stretch gap-2 sm:items-end">
              <ResourceRetirement
                resource={resource}
                activeRequestId={capabilityMeta.active_request_id || capabilities?.delete_at_provider?.active_request_id}
                activeOperationId={capabilityMeta.active_operation_id}
                onChanged={() => loadResource()}
              />
            </div>
          </div>
          
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 bg-${resourceConfig.color}-500/20 rounded-xl flex items-center justify-center`}>
                <IconComponent className={`w-8 h-8 text-${resourceConfig.color}-400`} />
              </div>
              <div>
                <h1 className="text-3xl font-semibold mb-1 tracking-tight text-zinc-100">{resource?.name}</h1>
                <p className="text-zinc-400">
                  {resourceConfig.label} • {resource?.region}
                </p>
                {resource?.tags && resource.tags.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {resource.tags.map((tag, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-zinc-800 rounded-full text-zinc-400">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
              resource?.stale || !['running', 'stopped'].includes(resource?.status)
                ? 'bg-zinc-800 text-zinc-300 border border-zinc-700'
                : resource?.status === 'running' ? 'status-running' : 'status-stopped'
            }`}>
              {resource?.status}{resource?.stale ? ' · stale' : ''}
            </div>
          </div>

          {(resource?.stale || resource?.sync_error || resource?.desired_status) && (
            <div className="mb-6 rounded-lg border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200" role="status">
              {resource?.desired_status
                ? `Provider operation pending; waiting to observe ${resource.desired_status}. `
                : 'This is the last known state. '}
              {resource?.sync_error || ''}
              {resource?.observed_at ? ` Last observed ${new Date(resource.observed_at).toLocaleString()}.` : ''}
              {resource?.desired_status && <Link to={`/work${capabilities?.start_request?.active_request_id || capabilities?.stop_request?.active_request_id ? `?request=${encodeURIComponent(capabilities?.start_request?.active_request_id || capabilities?.stop_request?.active_request_id)}` : ''}`} className="ml-2 font-medium underline">View in My Work</Link>}
            </div>
          )}

          <Dialog open={Boolean(powerIntent)} onOpenChange={(open) => { if (!open && !actionLoading) setPowerIntent(null); }}>
          {powerIntent && (
              <DialogContent className="w-full max-w-lg rounded-2xl border-zinc-800 bg-zinc-900 text-zinc-100">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-amber-400">Governed operation</p>
                <DialogTitle className="mt-2 text-xl font-semibold">Request to {powerIntent.action} {resource?.name}</DialogTitle>
                <DialogDescription className="mt-2 text-sm leading-6 text-zinc-400">Cloud Watcher will record the reason, apply the project approval policy, and only show completion after the provider state is confirmed.</DialogDescription>
                <label htmlFor="power-reason" className="mt-5 block text-sm font-medium text-zinc-300">Reason</label>
                <textarea id="power-reason" autoFocus rows={4} maxLength={500} value={powerReason} onChange={(event) => setPowerReason(event.target.value)} placeholder="Why is this change needed?" className="mt-2 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-500/60" />
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => setPowerIntent(null)} disabled={actionLoading} className="rounded-lg px-4 py-2 text-sm text-zinc-400 hover:bg-zinc-800 disabled:opacity-50">Cancel</button>
                  <button onClick={submitPowerRequest} disabled={actionLoading || powerReason.trim().length < 3} className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-medium text-zinc-950 disabled:cursor-not-allowed disabled:opacity-40">{actionLoading ? 'Submitting…' : 'Submit request'}</button>
                </div>
              </DialogContent>
          )}
          </Dialog>

          {resource?.management_mode === 'external' && (
            <div className="mb-6 rounded-lg border border-blue-500/20 bg-blue-500/10 p-4 text-sm text-blue-200" role="status">
              This existing server is tracked as external inventory. Cloud Watcher will not send provider power or deletion commands. Configure its Terminal connection for secure access; removing it here only removes the inventory record.
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-4 overflow-x-auto border-b border-zinc-800" role="tablist" aria-label="Resource details">
            <button
              onClick={() => setActiveTab('overview')}
              role="tab"
              aria-selected={activeTab === 'overview'}
              className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
                activeTab === 'overview' ? 'text-white' : 'text-zinc-400 hover:text-zinc-300'
              }`}
            >
              Overview
              {activeTab === 'overview' && (
                <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('context')}
              role="tab"
              aria-selected={activeTab === 'context'}
              className={`relative whitespace-nowrap px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === 'context' ? 'text-white' : 'text-zinc-400 hover:text-zinc-300'
              }`}
            >
              Context
              {activeTab === 'context' && (
                <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              role="tab"
              aria-selected={activeTab === 'applications'}
              className={`relative whitespace-nowrap px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === 'applications' ? 'text-white' : 'text-zinc-400 hover:text-zinc-300'
              }`}
            >
              Applications
              {activeTab === 'applications' && (
                <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
              )}
            </button>
            {resource?.type === 'vm' && (
              <button
                onClick={() => setActiveTab('ssh-access')}
                role="tab"
                aria-selected={activeTab === 'ssh-access'}
                className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
                  activeTab === 'ssh-access' ? 'text-white' : 'text-zinc-400 hover:text-zinc-300'
                }`}
              >
                SSH access
                {activeTab === 'ssh-access' && (
                  <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />
                )}
              </button>
            )}
            {resource?.type === 'vm' && (
              <button
                onClick={() => setActiveTab('terminal')}
                role="tab"
                aria-selected={activeTab === 'terminal'}
                className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
                  activeTab === 'terminal' ? 'text-white' : 'text-zinc-400 hover:text-zinc-300'
                }`}
              >
                Terminal
                {activeTab === 'terminal' && (
                  <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />
                )}
              </button>
            )}
          </div>
        </motion.div>

        {activeTab === 'context' ? (
          <div className="mt-6">
            <ResourceContext resourceId={id} canEdit={can('edit_metadata')} />
          </div>
        ) : activeTab === 'applications' ? (
          <div className="mt-6">
            <WorkloadDiscovery resourceId={id} canManage={can('edit_metadata')} onApplicationsChanged={() => setApplicationInventoryVersion((current) => current + 1)} />
            <ApplicationInventory key={`${id}:${applicationInventoryVersion}`} resourceId={id} canEdit={can('edit_metadata')} />
          </div>
        ) : activeTab === 'ssh-access' ? (
          <div className="mt-6">
            <DirectSshAccess resourceId={id} resourceName={resource?.name} />
          </div>
        ) : activeTab === 'terminal' ? (
          <div className="mt-6">
            <Terminal resourceId={id} resourceName={resource?.name} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Main Content - Metrics */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {renderMetrics({ historyMeta: metricHistoryMeta })}
              </motion.div>
            </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions - Only for actionable resources */}
            {resourceConfig.hasActions && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card rounded-xl p-6"
              >
                <h2 className="text-lg font-semibold text-zinc-200 mb-4">Actions</h2>
                <div className="space-y-3">
                  {can('start') && !resource?.desired_status ? (
                    <motion.button
                      whileHover={!actionLoading ? { scale: 1.02 } : {}}
                      whileTap={!actionLoading ? { scale: 0.98 } : {}}
                      onClick={() => handleAction('start')}
                      disabled={actionLoading}
                      className={`w-full px-4 py-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                        actionLoading 
                          ? 'bg-zinc-800 text-zinc-500 border-zinc-700 cursor-not-allowed' 
                          : 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30'
                      }`}
                    >
                      {actionLoading ? (
                        <div className="w-5 h-5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <LuPlay className="w-5 h-5" />
                      )}
                      {actionLoading ? 'Processing...' : 'Start'}
                    </motion.button>
                  ) : can('stop') && !resource?.desired_status ? (
                    <motion.button
                      whileHover={!actionLoading ? { scale: 1.02 } : {}}
                      whileTap={!actionLoading ? { scale: 0.98 } : {}}
                      onClick={() => handleAction('stop')}
                      disabled={actionLoading}
                      className={`w-full px-4 py-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                        actionLoading 
                          ? 'bg-zinc-800 text-zinc-500 border-zinc-700 cursor-not-allowed' 
                          : 'bg-orange-500/20 text-orange-400 border-orange-500/30 hover:bg-orange-500/30'
                      }`}
                    >
                      {actionLoading ? (
                        <div className="w-5 h-5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <LuSquare className="w-5 h-5" />
                      )}
                      {actionLoading ? 'Processing...' : 'Stop'}
                    </motion.button>
                  ) : (
                    <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-300">
                      <span className="mr-1 font-semibold">{capabilityStateLabel(observedPowerCapability)}.</span>
                      {capabilityReason(observedPowerCapability?.reason_code || observedPowerCapability?.reason)}
                      {observedPowerCapability?.reason_code === 'operation_pending' && observedPowerCapability?.active_request_id && (
                        <Link to={`/work?request=${encodeURIComponent(observedPowerCapability.active_request_id)}`} className="ml-1 font-medium underline">Open My Work</Link>
                      )}
                    </div>
                  )}
                  
                  <motion.button
                    whileHover={!actionLoading ? { scale: 1.02 } : {}}
                    whileTap={!actionLoading ? { scale: 0.98 } : {}}
                    onClick={() => handleSync()}
                    disabled={actionLoading || !can('sync')}
                    className={`w-full px-4 py-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                      actionLoading 
                        ? 'bg-zinc-800 text-zinc-500 border-zinc-700 cursor-not-allowed' 
                        : 'bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20'
                    }`}
                  >
                    {actionLoading ? (
                      <div className="w-5 h-5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <LuActivity className="w-5 h-5" />
                    )}
                    {!can('sync') ? 'Provider sync unavailable' : actionLoading ? 'Syncing...' : 'Sync Status'}
                  </motion.button>

                  <p className="text-xs leading-relaxed text-zinc-500">
                    Inventory retirement and provider deletion are governed separately in the controls above. Neither action removes the audit history.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Resource Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card rounded-xl p-6"
            >
              <h2 className="text-lg font-semibold text-zinc-200 mb-4">Details</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Resource ID</p>
                  <p className="font-mono text-sm break-all">{resource?.id}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Type</p>
                  <p className="font-medium">{resourceConfig.label}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Region</p>
                  <p className="font-medium">{resource?.region}</p>
                </div>
                {(resource?.config?.public_ip || resource?.config?.ip_address || resource?.config?.endpoint) && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
                      {resource?.type === 'database' ? 'Endpoint' : 'Public IP'}
                    </p>
                    <p className="font-mono text-sm text-blue-400 flex items-center gap-2">
                      {resource?.config?.public_ip || resource?.config?.ip_address || resource?.config?.endpoint}
                      <LuExternalLink className="w-3 h-3 opacity-50" />
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Monthly Cost</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold font-mono">{resourceCost === null ? 'Not available' : `$${resourceCost.toFixed(2)}`}</p>
                    {resourceCost !== null && ['cost_explorer', 'usage_api'].includes(resource?.cost_source) ? (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">ACTUAL</span>
                    ) : resourceCost !== null ? (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">EST</span>
                    ) : null}
                  </div>
                </div>
                {resource?.created_at && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Created</p>
                    <p className="text-sm">{new Date(resource?.created_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Cost Breakdown - Pie Chart */}
            {costBreakdown && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
                className="glass-card rounded-xl p-6"
              >
                <h2 className="text-lg font-semibold text-zinc-200 mb-4">Cost Breakdown</h2>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={costBreakdown.components}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      dataKey="cost"
                      nameKey="name"
                      label={({ name, percentage }) => `${percentage?.toFixed(0) || 0}%`}
                      labelLine={false}
                    >
                      {costBreakdown.components.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => `$${value.toFixed(2)}`}
                      contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {costBreakdown.components.map((component, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-zinc-400">{component.name}</span>
                      </div>
                      <span className="font-mono text-zinc-200">${component.cost.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="pt-2 mt-2 border-t border-zinc-800 flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-300">Total</span>
                    <span className="font-mono font-bold text-green-400">${costBreakdown.total.toFixed(2)}</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Specifications */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card rounded-xl p-6"
            >
              <h2 className="text-lg font-semibold text-zinc-200 mb-4">Specifications</h2>
              <div className="space-y-4">

                {/* ── Compute specs highlight row ── */}
                {(resource?.config?.vcpu || resource?.config?.ram_gb || resource?.config?.disk_gb) && (
                  <div className="grid grid-cols-3 gap-3 mb-2">
                    {resource.config.vcpu && (
                      <div className="bg-zinc-800/60 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-zinc-100">{resource.config.vcpu}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">vCPU</p>
                      </div>
                    )}
                    {resource.config.ram_gb && (
                      <div className="bg-zinc-800/60 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-zinc-100">{resource.config.ram_gb}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">GB RAM</p>
                      </div>
                    )}
                    {resource.config.disk_gb && (
                      <div className="bg-zinc-800/60 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-zinc-100">{resource.config.disk_gb}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">GB Disk</p>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Instance type / shape ── */}
                {(resource?.config?.raw_shape || resource?.config?.instance_type || resource?.config?.machine_type || resource?.config?.shape || resource?.config?.vm_size || resource?.config?.product_name) && (
                  <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                    <span className="text-sm text-zinc-400">Instance Type</span>
                    <span className="font-mono text-sm">
                      {resource.config.raw_shape || resource.config.instance_type || resource.config.machine_type || resource.config.shape || resource.config.vm_size || resource.config.product_name}
                    </span>
                  </div>
                )}

                {/* ── Architecture ── */}
                {resource?.config?.arch && (
                  <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                    <span className="text-sm text-zinc-400">Architecture</span>
                    <span className="font-mono text-sm">{resource.config.arch}</span>
                  </div>
                )}

                {/* ── OS ── */}
                {(resource?.config?.os_type || resource?.config?.platform) && (
                  <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                    <span className="text-sm text-zinc-400">OS</span>
                    <span className="font-mono text-sm">{resource.config.os_type || resource.config.platform}</span>
                  </div>
                )}

                {/* ── Network ── */}
                {(resource?.config?.public_ip || resource?.config?.ip_address) && (
                  <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                    <span className="text-sm text-zinc-400">Public IP</span>
                    <span className="font-mono text-sm text-blue-400">{resource.config.public_ip || resource.config.ip_address}</span>
                  </div>
                )}
                {resource?.config?.private_ip && (
                  <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                    <span className="text-sm text-zinc-400">Private IP</span>
                    <span className="font-mono text-sm">{resource.config.private_ip}</span>
                  </div>
                )}

                {/* ── Cost breakdown ── */}
                {costBreakdown && (
                  <>
                    <div className="pt-2 mt-2 border-t border-zinc-700">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Cost Breakdown</p>
                    </div>
                    {costBreakdown.components.map((c, i) => (
                      <div key={i} className="flex justify-between items-center border-b border-zinc-800 pb-2">
                        <div>
                          <span className="text-sm text-zinc-400">{c.name}</span>
                          {c.description && <p className="text-xs text-zinc-600">{c.description}</p>}
                        </div>
                        <span className="font-mono text-sm text-amber-400">${c.cost?.toFixed(2)}/mo</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-sm font-medium text-zinc-300">Total</span>
                      <span className="font-mono text-sm font-bold text-amber-300">${resourceCost.toFixed(2)}/mo</span>
                    </div>
                  </>
                )}

                {/* ── Provider details ── */}
                {(resource?.config?.availability_domain || resource?.config?.fault_domain || resource?.config?.data_center || resource?.config?.resource_group || resource?.config?.zone) && (
                  <>
                    <div className="pt-2 mt-2 border-t border-zinc-700">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Provider Details</p>
                    </div>
                    {resource?.config?.zone && (
                      <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                        <span className="text-sm text-zinc-400">Zone</span>
                        <span className="font-mono text-xs text-zinc-500">{resource.config.zone}</span>
                      </div>
                    )}
                    {resource?.config?.availability_domain && (
                      <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                        <span className="text-sm text-zinc-400">Availability Domain</span>
                        <span className="font-mono text-xs text-zinc-500">{resource.config.availability_domain}</span>
                      </div>
                    )}
                    {resource?.config?.fault_domain && (
                      <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                        <span className="text-sm text-zinc-400">Fault Domain</span>
                        <span className="font-mono text-xs text-zinc-500">{resource.config.fault_domain}</span>
                      </div>
                    )}
                    {resource?.config?.data_center && (
                      <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                        <span className="text-sm text-zinc-400">Data Center</span>
                        <span className="font-mono text-xs text-zinc-500">{resource.config.data_center}</span>
                      </div>
                    )}
                    {resource?.config?.resource_group && (
                      <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                        <span className="text-sm text-zinc-400">Resource Group</span>
                        <span className="font-mono text-xs text-zinc-500">{resource.config.resource_group}</span>
                      </div>
                    )}
                    {(resource?.config?.time_created || resource?.config?.created_date || resource?.config?.creation_timestamp) && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-zinc-400">Created</span>
                        <span className="font-mono text-xs text-zinc-500">
                          {new Date(resource.config.time_created || resource.config.created_date || resource.config.creation_timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* ── Fallback ── */}
                {!resource?.config?.vcpu && !resource?.config?.raw_shape && !resource?.config?.instance_type && !resource?.config?.machine_type && !resource?.config?.shape && (
                  <p className="text-sm text-zinc-500 italic">No specification details available.</p>
                )}
              </div>
            </motion.div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default ResourceDetail;
